import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Plus, Calendar as CalIcon, Trash2, Clock, MapPin, Target, ClipboardList, TrendingUp, FolderKanban, Compass, ChevronLeft, ChevronRight } from "lucide-react";
import Seo from "@/components/Seo";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary";

function fmtTime(t) {
  if (!t) return "";
  return t;
}
function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

const KIND_META = {
  event: { icon: CalIcon, tone: "text-brand-teal", label: "Event" },
  task: { icon: ClipboardList, tone: "text-brand-teal", label: "Task" },
  follow_up: { icon: TrendingUp, tone: "text-brand-orange", label: "Follow-up" },
  deal_close: { icon: TrendingUp, tone: "text-brand-teal", label: "Deal close" },
  project_deadline: { icon: FolderKanban, tone: "text-brand-red", label: "Project" },
  milestone: { icon: FolderKanban, tone: "text-brand-teal", label: "Milestone" },
  goal_deadline: { icon: Compass, tone: "text-brand-teal", label: "Goal" },
};

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function ymd(d) {
  return d.toISOString().slice(0, 10);
}
function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function startOfWeek(d) {
  const day = d.getDay();
  const offset = day === 0 ? -6 : 1 - day; // Monday-first
  return addDays(d, offset);
}

export default function CalendarEngine() {
  const navigate = useNavigate();
  const [view, setView] = useState("agenda"); // agenda | week | month
  const [anchor, setAnchor] = useState(new Date());
  const [items, setItems] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: "", starts_at: "", location: "", description: "" });

  const range = useMemo(() => {
    if (view === "week") {
      const s = startOfWeek(anchor);
      return { start: ymd(s), end: ymd(addDays(s, 6)) };
    }
    if (view === "month") {
      return { start: ymd(startOfMonth(anchor)), end: ymd(endOfMonth(anchor)) };
    }
    return { start: ymd(addDays(new Date(), -7)), end: ymd(addDays(new Date(), 60)) };
  }, [view, anchor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, e] = await Promise.all([
        api.get(`/calendar/unified?start=${range.start}&end=${range.end}`),
        api.get("/calendar/events"),
      ]);
      setItems(u.data.items || []);
      setEvents(e.data.events || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load calendar.");
    } finally {
      setLoading(false);
    }
  }, [range.start, range.end]);
  useEffect(() => { load(); }, [load]);

  const addEvent = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.starts_at) return;
    try {
      await api.post("/calendar/events", {
        title: form.title.trim(),
        starts_at: new Date(form.starts_at).toISOString(),
        location: form.location,
        description: form.description,
      });
      setForm({ title: "", starts_at: "", location: "", description: "" });
      setShowAdd(false);
      toast.success("Event added");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create event.");
    }
  };

  const removeEvent = async (id) => {
    try {
      await api.delete(`/calendar/events/${id}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete event.");
    }
  };

  const openItem = (item) => {
    if (item.route) navigate(item.route);
  };

  // Group items by date
  const byDate = useMemo(() => {
    const g = {};
    for (const it of items) {
      if (!it.date) continue;
      (g[it.date] = g[it.date] || []).push(it);
    }
    return g;
  }, [items]);

  // Build month grid
  const monthCells = useMemo(() => {
    if (view !== "month") return [];
    const s = startOfMonth(anchor);
    const gridStart = startOfWeek(s);
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = addDays(gridStart, i);
      const key = ymd(d);
      cells.push({ date: d, key, inMonth: d.getMonth() === anchor.getMonth(), items: byDate[key] || [] });
    }
    return cells;
  }, [view, anchor, byDate]);

  const weekDays = useMemo(() => {
    if (view !== "week") return [];
    const s = startOfWeek(anchor);
    return Array.from({ length: 7 }, (_, i) => {
      const d = addDays(s, i);
      const key = ymd(d);
      return { date: d, key, items: byDate[key] || [] };
    });
  }, [view, anchor, byDate]);

  return (
    <div data-testid="calendar-engine">
      <Seo title="Calendar" description="Meetings, events, tasks, follow-ups, project & goal deadlines." path="/app/calendar" />
      <header className="mb-6">
        <p className="eyebrow text-brand-teal">Calendar</p>
        <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight">Your entire week, in one place.</h1>
        <p className="mt-2 text-sm text-muted-foreground">{items.length} items · events, tasks, follow-ups, deadlines & milestones</p>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center gap-1 rounded-full border border-border bg-card p-1" data-testid="cal-view-toggle">
          {[["agenda", "Agenda"], ["week", "Week"], ["month", "Month"]].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setView(k)}
              data-testid={`cal-view-${k}`}
              className={`rounded-full px-3 py-1 text-xs font-semibold ${view === k ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
            >
              {l}
            </button>
          ))}
        </div>

        {view !== "agenda" && (
          <div className="ml-auto inline-flex items-center gap-1">
            <button onClick={() => setAnchor(addDays(anchor, view === "week" ? -7 : -30))} className="rounded-full border border-border bg-card p-1 text-muted-foreground hover:text-foreground" data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={() => setAnchor(new Date())} className="rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold" data-testid="cal-today">Today</button>
            <button onClick={() => setAnchor(addDays(anchor, view === "week" ? 7 : 30))} className="rounded-full border border-border bg-card p-1 text-muted-foreground hover:text-foreground" data-testid="cal-next"><ChevronRight className="h-4 w-4" /></button>
            <span className="ml-2 text-xs font-medium text-muted-foreground">{anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</span>
          </div>
        )}

        <button
          onClick={() => setShowAdd((v) => !v)}
          data-testid="event-add-open"
          className="ml-auto inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-3 py-1.5 text-xs font-semibold text-white"
        >
          <Plus className="h-3.5 w-3.5" /> Add event
        </button>
      </div>

      {showAdd && (
        <form onSubmit={addEvent} className="mb-4 grid gap-2 rounded-2xl border border-border bg-card p-3 md:grid-cols-2" data-testid="event-form">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Event title…" className={inputCls} data-testid="event-title-input" />
          <input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className={inputCls} data-testid="event-starts-input" />
          <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Location (optional)" className={inputCls} data-testid="event-location-input" />
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Notes (optional)" className={inputCls} data-testid="event-description-input" />
          <div className="md:col-span-2">
            <button type="submit" data-testid="event-add-btn" className="inline-flex items-center gap-1.5 rounded-xl brand-gradient-bg px-4 py-2 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> Add event</button>
          </div>
        </form>
      )}

      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : (
        <>
          {view === "agenda" && (
            <div className="space-y-4" data-testid="cal-agenda">
              {Object.keys(byDate).sort().map((d) => (
                <section key={d}>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{fmtDate(d)}</h3>
                  <div className="space-y-2">
                    {byDate[d].map((it) => (
                      <UnifiedRow key={it.id} item={it} onOpen={() => openItem(it)} onDelete={it.kind === "event" ? () => removeEvent(it.source_id) : null} />
                    ))}
                  </div>
                </section>
              ))}
              {Object.keys(byDate).length === 0 && (
                <p className="text-sm text-muted-foreground">No items in your window. Add tasks, follow-ups or deadlines and they'll show up here.</p>
              )}
            </div>
          )}

          {view === "week" && (
            <div className="grid gap-3 md:grid-cols-7" data-testid="cal-week">
              {weekDays.map((day) => (
                <div key={day.key} className="rounded-2xl border border-border bg-card p-3 min-h-[120px]">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{day.date.toLocaleDateString(undefined, { weekday: "short" })}</p>
                  <p className="font-display text-lg font-semibold">{day.date.getDate()}</p>
                  <div className="mt-2 space-y-1">
                    {day.items.map((it) => <MiniItem key={it.id} item={it} onClick={() => openItem(it)} />)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {view === "month" && (
            <div className="grid grid-cols-7 gap-1" data-testid="cal-month">
              {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => (
                <div key={d} className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground text-center">{d}</div>
              ))}
              {monthCells.map((c) => (
                <div key={c.key} className={`rounded-lg border p-1.5 min-h-[80px] text-[11px] ${c.inMonth ? "border-border bg-card" : "border-transparent bg-muted/20 text-muted-foreground/60"}`} data-testid={`cal-month-cell-${c.key}`}>
                  <p className="font-semibold">{c.date.getDate()}</p>
                  <div className="mt-0.5 space-y-0.5">
                    {c.items.slice(0, 3).map((it) => (
                      <button key={it.id} onClick={() => openItem(it)} className="block w-full truncate rounded bg-muted/70 px-1 py-0.5 text-left hover:bg-brand-teal/10">
                        {it.title}
                      </button>
                    ))}
                    {c.items.length > 3 && <p className="text-[10px] text-muted-foreground">+{c.items.length - 3} more</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function UnifiedRow({ item, onOpen, onDelete }) {
  const meta = KIND_META[item.kind] || KIND_META.event;
  const Icon = meta.icon;
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-border bg-card p-3" data-testid={`cal-item-${item.id}`}>
      <span className={`mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-muted ${meta.tone}`}>
        <Icon className="h-4 w-4" />
      </span>
      <button onClick={onOpen} className="min-w-0 flex-1 text-left" data-testid={`cal-item-open-${item.id}`}>
        <p className="truncate text-sm font-semibold">{item.title}</p>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
          <span className="rounded bg-muted/60 px-1.5 py-0.5 font-medium">{meta.label}</span>
          {item.time && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtTime(item.time)}</span>}
          {item.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {item.location}</span>}
          {item.priority && <span className="capitalize">{item.priority}</span>}
          {item.stage && <span className="capitalize">{item.stage}</span>}
        </p>
      </button>
      {onDelete && (
        <button onClick={onDelete} className="text-muted-foreground hover:text-destructive" data-testid={`cal-delete-${item.id}`}>
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

function MiniItem({ item, onClick }) {
  const meta = KIND_META[item.kind] || KIND_META.event;
  return (
    <button onClick={onClick} className={`block w-full truncate rounded px-1.5 py-1 text-left text-[11px] font-medium hover:bg-brand-teal/10 ${meta.tone}`} data-testid={`cal-mini-${item.id}`}>
      {item.title}
    </button>
  );
}
